// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import { UilTrash, UilPen, UilInfoCircle, UilDocumentInfo, UilMinusCircle, UilPlusCircle } from '@iconscout/react-unicons';
import { toast } from 'react-toastify';
import { Form, Formik, ErrorMessage, FieldArray } from 'formik';
import _ from 'lodash';

// Internal module imports
import Typography from '../../../components/Typography/Typography';
import Table from '../../../components/Table/Table';
import { useLoader } from '../../../hooks';
import DeleteModal from '../../../components/DeleteModal/DeleteModal';
import { Input, ToggleButton } from '../../../components/Inputs/Input';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import { instanceFormValidation } from '../../../validations/Home/instanceValidation';
import DropdownComponent from '../../../components/Inputs/Dropdown';
import { DropdownComponent as DropDownWithoutFormik } from '../../../components/Inputs/Input';
import { CamelCaseToTextCase } from '../../../utils/stringHelper';
import { TextArea } from '../../../components/Inputs/Input';
import { InstanceService } from '../../../services/instanceService';
import MultiSelectDropdown from '../../../components/Inputs/MultiSelectDropdown';
import { LicenseService } from '../../../services/LicenseService';

import classes from '../../../styles/AllDevices.module.css';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import NewJSONEditor from '../../../components/JSONEditor/NewJSONEditor';
import Info from './ModalComponent/Info';
import DocumentEditor from './ModalComponent/DocumentEditor';
import { COUNTRY_CODES, FUEL_TYPE, GEO_TAGS } from '../../../constants';
import instanceClasses from './index.module.css';
import DatePickerWithFormik from '../../../components/Inputs/DatePicker/DatePickerWithFormik';
import { DataHubService } from '../../../services/DataHubService';
import { checkValidNumber } from '../../../utils/numberHelper';
import TimePickerWithFormik from '../../../components/Inputs/TimePicker/TimePickerWithFormik';
import { findDifferenceInHoursAndMinutesFormatted } from '../../../utils/dateHelper';

const countryOption = COUNTRY_CODES.map((country) => {
    return { label: country.name, value: country.name };
});

const approvalOptions = [
    { label: 'Approved', value: 'approved' },
    { label: 'Unapproved', value: 'unapproved' },
    { label: 'All', value: 'All' },
];

const defaultValue = {
    label: 'None',
    value: '',
};

const defaultMetaValues = {
    connectorDetails: [],
    // bess2 keys
    minSOC: 0,
    maxSOC: 0,
    autoCharging: false,
    autoAvailability: false,
    chargingTime: 0,
    batteryStorageCapacity: 0,
    autoPurchasePower: false,
    batteryChargingPowerKW: 0,
    batteryMaxChargingPowerKW: 0,
    linkedInstance: [],
};

const getMarketOptions = (markets = [], inventoryValues = {}) => {
    const temp = [];
    markets.forEach((market) => temp.push({ label: market, value: market, isDisabled: _.get(inventoryValues, [market]) ? true : false }));
    return temp;
};

const daysForSchedule = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const getAreaOption = (areas = []) => {
    const temp = [];

    areas.map((area) => {
        temp.push({ label: area, value: area });
    });
    return temp;
};

function Instances({ customer }) {
    const [areaOption, setAreaOption] = useState(getAreaOption(customer?.areas || []));

    const [startLoader, stopLoader] = useLoader();
    const defaultOption = {
        label: 'All',
        value: 'All',
    };
    const [licenseOptions, setLicenseOptions] = useState([defaultOption]);
    const [selectedOption, setSelectedOption] = useState(defaultOption);
    const [unfiltered, setUnfiltered] = useState([]);

    const [instanceFormModal, setInstanceFormModal] = useState({
        status: false,
        data: '',
    });
    const [deleteModal, setDeleteModal] = useState({
        status: false,
        id: '',
    });
    const [licenseTypes, setLicenseTypes] = useState([]);
    const [licenseTypesHashMap, setLicenseTypesHashMap] = useState({});
    const [markets, setMarkets] = useState([]);
    const [instances, setInstances] = useState([]);
    const [infoModal, setInfoModal] = useState({
        status: false,
        data: {},
    });
    const [isOpenDocumentModal, setOpenDocumentModal] = useState({
        status: false,
        data: {},
    });

    const [selectedApproval, setSelectedApproval] = useState(approvalOptions[0]);

    const initialValues = {
        name: instanceFormModal.data?.name ? instanceFormModal.data.name : '',
        description: instanceFormModal.data?.description ? instanceFormModal.data.description : '',
        licenseId: instanceFormModal.data?.licenseId?._id ? instanceFormModal.data.licenseId._id : '',
        market: instanceFormModal.data?.market
            ? instanceFormModal.data.market.map((item) => ({
                  label: item,
                  value: item,
              }))
            : [],
        meta: instanceFormModal.data?.meta ? instanceFormModal.data.meta : {},
        metaErrorKeys: {},
        inMarket: instanceFormModal.data?.inMarket || instanceFormModal.data?.inMarket === false ? instanceFormModal.data.inMarket : false,
        inLedger: _.get(instanceFormModal, ['data', 'inLedger'], false),
        approvedCapacity: instanceFormModal.data?.approvedCapacity || {},
        webshopUrl: instanceFormModal.data?.webshopUrl || [''],
        jiraLink: instanceFormModal.data?.jiraLink || '',
        address: instanceFormModal.data?.address || {
            floor: null,
            city: null,
            pincode: null,
            streetName: null,
            country: _.get(countryOption, [0, 'value'], null),
        },
        approvalValidity: instanceFormModal?.data?.approvalValidity || {},
        inventoryValue: instanceFormModal?.data?.inventoryValue ? instanceFormModal?.data?.inventoryValue : {},
        geoTag: instanceFormModal.data?.geoTag || '',
        datahubMeters: _.get(instanceFormModal, ['data', 'datahubMeters', 0], ''),
        fuelType: instanceFormModal.data?.fuelType || '',
        dailyChargingSchedule: instanceFormModal.data?.dailyChargingSchedule || {},
        area: instanceFormModal.data?.area || '',
        resourceSchedule: _.get(instanceFormModal, ['data', 'resourceSchedule'], false),
        energyMarketOnly: _.get(instanceFormModal, ['data', 'energyMarketOnly'], false),
    };
    const [defaultMetaKeys, setDefaultMetaKeys] = useState({});
    const [updateDefaultValueEditor, setUpdateDefaultValueEditor] = useState(false);

    const [datahubMeterOptions, setDatahubMeterOptions] = useState([]);
    const [filteredDatahubMeterOptions, setFilteredDatahubMeterOptions] = useState([]);
    const [datahubMeterDefaultValue, setDatahubMeterDefaultValue] = useState({ value: '' });

    const [editInventory, setEditInventory] = useState({});

    useEffect(() => {
        searchFilter(unfiltered);
    }, [selectedOption]);

    useEffect(() => {
        if (instanceFormModal.status && customer?._id) getDatahubMeters();
        if (instanceFormModal.status && instanceFormModal.data?.market?.length) {
            const temp = {};
            instanceFormModal.data?.market?.map((m) => {
                _.set(temp, [m], true);
            });
            setEditInventory(temp);
        }
        if (instanceFormModal.status) setDatahubMeterDefaultValue({ value: _.get(instanceFormModal, ['data', 'datahubMeters', 0], '') });
    }, [instanceFormModal.status]);

    useEffect(() => {
        let markets = [];
        if (customer.biddingMarkets?.length > 0 && instanceFormModal.status) markets = getMarketOptions(customer.biddingMarkets, instanceFormModal?.data?.inventoryValue);

        markets.length > 0 ? setMarkets(markets) : setMarkets([]);
    }, [instanceFormModal.status]);

    useEffect(() => {
        getAllInstances();
        getLicenseTypes();
    }, []);

    useEffect(() => {
        getAllInstances();
    }, [selectedApproval.value]);

    const searchFilter = (data) => {
        if (selectedOption.value === 'All') {
            setInstances(data);
        } else {
            const filtered = data.filter((instance) => instance.licenseType === selectedOption.value).map((instance, index) => ({ ...instance, 's.no': index + 1 }));
            setInstances(filtered);
        }
    };

    const getLicenseTypes = () => {
        LicenseService.ReadV2(customer._id, startLoader, handleLicenseTypesSuccess, handleError, stopLoader);
    };

    const handleLicenseTypesSuccess = ({ data }) => {
        const licenseTypes = [];
        const licenseTypeHashMap = {};
        data.data?.forEach((license) => {
            _.set(licenseTypeHashMap, license._id, license.name);
            licenseTypes.push({
                label: CamelCaseToTextCase(license.name),
                value: license._id,
                metaKeys: license?.defaultMetaKeys || [],
            });
        });
        setLicenseTypes(licenseTypes);
        setLicenseTypesHashMap(licenseTypeHashMap);
    };

    const handleInstanceSuccess = ({ data }) => {
        const licenseTypes = new Set([]);
        const tableData = data?.data.map((item, index) => {
            licenseTypes.add(CamelCaseToTextCase(_.get(item, ['licenseId', 'name'], '')));
            return {
                's.no': index + 1,
                name: item?.name,
                instanceId: item._id,
                description: !item?.description ? (
                    '--'
                ) : item.description.length > 40 ? (
                    <CustomTooltip content={item.description}>{item.description.substring(0, 40) + '...'}</CustomTooltip>
                ) : (
                    item.description
                ),
                licenseType: CamelCaseToTextCase(_.get(item, ['licenseId', 'name'], '')),
                market: item?.market.length ? item?.market.join(', ') : '--',
                monitoring: item?.monitoring,
                active: item?.active,
                action: (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <div>
                            <CustomTooltip content={'Info'}>
                                <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setInfoModal({ status: true, data: item })} />
                            </CustomTooltip>
                        </div>
                        <div style={{ marginLeft: '0.3vw' }}>
                            <CustomTooltip content={'Document'}>
                                <UilDocumentInfo
                                    size={'1.2vw'}
                                    style={{ color: 'var(--color-primary)' }}
                                    onClick={() => {
                                        setOpenDocumentModal({ status: true, data: item });
                                    }}
                                />
                            </CustomTooltip>
                        </div>
                        <div style={{ marginLeft: '0.3vw' }}>
                            <CustomTooltip content={'Edit'}>
                                <UilPen
                                    size={'1.2vw'}
                                    style={{ color: 'var(--color-primary)' }}
                                    onClick={() => {
                                        setInstanceFormModal({
                                            status: true,
                                            data: item,
                                        });
                                    }}
                                />
                            </CustomTooltip>
                        </div>

                        <div style={{ marginLeft: '0.3vw' }}>
                            <CustomTooltip content={'Delete'}>
                                <UilTrash size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setDeleteModal({ status: true, id: item._id })} />
                            </CustomTooltip>
                        </div>
                    </div>
                ),
            };
        });
        searchFilter(tableData);
        const licenseDropOptions = [defaultOption];
        licenseTypes.forEach((type) => {
            licenseDropOptions.push({ label: type, value: type });
        });
        setLicenseOptions(licenseDropOptions);
        setUnfiltered(tableData);
    };

    const getAllInstances = () => {
        const params = {
            approval: selectedApproval.value,
        };
        InstanceService.ReadAll(customer._id, params, startLoader, handleInstanceSuccess, handleError, stopLoader);
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    const handleSuccess = (res) => {
        if (res) {
            toast.success(res);
            getAllInstances();
            setInstanceFormModal({ status: false, data: '' });
        }
    };

    const handleFormSubmit = (data) => {
        const bodyData = { customerId: customer._id, ...data };
        bodyData.market = data.market.map((item) => item.value);
        bodyData.datahubMeters = data?.datahubMeters?.length ? [data.datahubMeters] : [];
        const tempInventory = {};
        bodyData.market.map((m) => {
            const val = checkValidNumber(_.get(bodyData, ['inventoryValue', m]));
            _.set(tempInventory, m, val);
        });
        bodyData.inventoryValue = tempInventory;
        if (!instanceFormModal.data) InstanceService.Create(bodyData, startLoader, () => handleSuccess('Instance Created Successfully'), handleError, stopLoader);
        else InstanceService.Update(instanceFormModal.data._id, bodyData, startLoader, () => handleSuccess('Instance Updated Successfully'), handleError, stopLoader);
    };

    const handleDelete = () => {
        InstanceService.Delete(deleteModal.id, startLoader, () => handleSuccess('Instance Deleted Successfully'), handleError, stopLoader);
    };

    const getGeoTagOption = (markets) => {
        let temp = [defaultValue];
        const area = [];
        let isAfrrMarketSelected = false;
        markets.map((m) => {
            const value = m.value;
            if (value.includes('aFRR') || value.includes('mFRR')) {
                isAfrrMarketSelected = true;
                if (value.includes('DK1')) area.push('DK1');
                if (value.includes('DK2')) area.push('DK2');
            }
        });

        if (isAfrrMarketSelected) {
            GEO_TAGS.map((gt) => {
                if (area.includes(gt.area)) {
                    temp.push({
                        label: `${gt.value}, ${gt.area}, ${gt.description}`,
                        value: gt.value,
                    });
                }
            });
        }

        return temp;
    };

    const getDatahubMeters = () => {
        DataHubService.getDatahubMetersForCustomer(customer._id, startLoader, handleDatahubMeterSuccess, handleError, stopLoader);
    };

    const handleDatahubMeterSuccess = ({ data }) => {
        const proccessData = _.get(data, ['data'], []).map((el) => ({
            label: `${el.dhCustomerName} - ${el.meterName} -${el.meterId}`,
            value: el.meterId,
            meterType: el.meterType,
        }));
        const allMeterOptions = [{ label: 'None', value: '' }, ...proccessData];
        setDatahubMeterOptions(allMeterOptions);
        setFilteredDatahubMeterOptions(filterDatahubMeterOptions(allMeterOptions, instanceFormModal.data?.market));
    };

    const filterDatahubMeterOptions = (options, markets = []) => {
        const marketValues = markets.map((m) => (typeof m === 'string' ? m : m?.value) || '').filter(Boolean);
        if (!marketValues.length) return options.filter((option) => !option.value);

        const relevantValues = marketValues.filter((v) => v.includes('FRR') || v.includes('FCR-N'));
        if (!relevantValues.length) return options;

        const allowedMeterTypes = new Set();

        if (relevantValues.some((v) => v.includes('FCR-N'))) allowedMeterTypes.add('E17');
        if (relevantValues.some((v) => v.includes('INC'))) allowedMeterTypes.add('E17');
        if (relevantValues.some((v) => v.includes('DEC'))) allowedMeterTypes.add('E18');

        if (!allowedMeterTypes.size) return options;

        return options.filter((option) => !option.meterType || allowedMeterTypes.has(option.meterType));
    };

    return (
        <div className={classes.AllDevices}>
            <DeleteModal
                deletefunction={handleDelete}
                opendeleteModal={deleteModal.status}
                setOpenDeleteModal={(status) => setDeleteModal({ ...deleteModal, status })}
            ></DeleteModal>
            <ModalComponent
                isOpen={infoModal.status}
                setOpen={() => {
                    setInfoModal({ status: false, data: {} });
                }}
            >
                <div>
                    <Info data={infoModal.data} setInfoModal={setInfoModal} />
                </div>
            </ModalComponent>
            <ModalComponent isOpen={isOpenDocumentModal.status} setOpen={() => {}}>
                <div style={{ overflow: 'auto' }}>
                    <DocumentEditor instance={isOpenDocumentModal.data} setOpen={setOpenDocumentModal} />
                </div>
            </ModalComponent>
            <ModalComponent
                isOpen={instanceFormModal.status}
                setOpen={(e) => {
                    setInstanceFormModal({ status: false, data: '' });
                }}
                style={{ overflow: 'initial' }}
            >
                <Typography content={instanceFormModal.data ? 'Edit Instance' : 'Create Instance'} />
                <div style={{ width: '40vw', height: '80vh', overflow: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={instanceFormValidation(defaultMetaKeys, daysForSchedule, _.get(instanceFormModal, ['data', 'licenseId', 'name'], ''))}
                        onSubmit={handleFormSubmit}
                        enableReinitialize
                    >
                        {({ errors, touched, values, isValidating, ...props }) => {
                            return (
                                <Form>
                                    <div className={classes.FieldControl2}>
                                        <label for="name">
                                            Name <span className="required">*</span>
                                        </label>
                                        <Input
                                            name="name"
                                            id="name"
                                            type="text"
                                            style={{
                                                marginTop: '0',
                                                padding: '0.1vw 0 0.1vw 0.5vw',
                                            }}
                                        />
                                    </div>
                                    <div className={classes.FieldControl2}>
                                        <label for="description">Description</label>
                                        <TextArea name="description" type="text" />
                                    </div>
                                    <div className={classes.FieldControl2}>
                                        <label for="licenseId">
                                            Licenses Type<span className="required">*</span>
                                        </label>
                                        <DropdownComponent
                                            name="licenseId"
                                            id="licenseId"
                                            options={licenseTypes}
                                            defaultValue={{ value: initialValues.licenseId }}
                                            onChange={(e) => {
                                                setUpdateDefaultValueEditor(true);
                                                let temp = {};
                                                e?.metaKeys?.map((key) => {
                                                    const defaultValue = defaultMetaValues.hasOwnProperty(key) ? defaultMetaValues[key] : 'Enter value';
                                                    temp[key] = initialValues?.meta[key] || defaultValue;
                                                });
                                                props.setFieldValue('meta', {
                                                    ...temp,
                                                    ...initialValues?.meta,
                                                });
                                                props.setFieldValue('metaErrorKeys', temp);
                                                setDefaultMetaKeys(temp);
                                            }}
                                        />
                                    </div>
                                    <div className={classes.FieldControl2}>
                                        <label for="area">
                                            Area<span className="required">*</span>
                                        </label>
                                        <DropdownComponent name="area" id="area" options={areaOption} defaultValue={{ value: initialValues?.area }} />
                                    </div>
                                    <div className={classes.FieldControl2}>
                                        <label for="market">
                                            Market<span className="required">*</span>
                                        </label>
                                        <MultiSelectDropdown
                                            name="market"
                                            options={markets}
                                            defaultValue={values?.market}
                                            onChange={(e) => {
                                                if (e?.length) {
                                                    const tempKey = _.keys(editInventory);
                                                    const temp = editInventory;
                                                    e.map((m) => {
                                                        if (!tempKey.includes(m.value)) temp[m.value] = true;
                                                    });
                                                    setEditInventory(temp);
                                                }
                                                if (datahubMeterOptions.length) {
                                                    const newFilteredOptions = filterDatahubMeterOptions(datahubMeterOptions, e);
                                                    setFilteredDatahubMeterOptions(newFilteredOptions);
                                                    if (values.datahubMeters && !newFilteredOptions.some((option) => option.value === values.datahubMeters)) {
                                                        props.setFieldValue('datahubMeters', '');
                                                        setDatahubMeterDefaultValue({ value: '' });
                                                    }
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className={classes.FieldControl2}>
                                        <label for="datahubMeters">Datahub Meter</label>
                                        <DropdownComponent name="datahubMeters" id="datahubMeters" options={filteredDatahubMeterOptions} defaultValue={datahubMeterDefaultValue} />
                                    </div>

                                    <div>
                                        {values.market.length ? (
                                            <div className={classes.FieldControl2}>
                                                <label for="approval">Approval Validity</label>
                                            </div>
                                        ) : (
                                            ''
                                        )}

                                        {values.market.map((item) => (
                                            <div className={classes.InputContainer}>
                                                <div className={classes.FieldControl2}>
                                                    <label for="startDate">Start Date ({item.value})</label>
                                                    <div className="modal-date-picker">
                                                        <DatePickerWithFormik
                                                            name={`approvalValidity.${item.value}.startDate`}
                                                            date={_.get(values, ['approvalValidity', item.value, 'startDate'], null)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className={classes.FieldControl2}>
                                                    <label for="endDate">End Date ({item.value})</label>
                                                    <div className="modal-date-picker">
                                                        <DatePickerWithFormik
                                                            name={`approvalValidity.${item.value}.endDate`}
                                                            date={_.get(values, ['approvalValidity', item.value, 'endDate'], null)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label for="meta">Meta</label>
                                            <NewJSONEditor
                                                name="meta"
                                                defaultValue={values.meta}
                                                manualDefaultValueUpdate={updateDefaultValueEditor}
                                                setManualDefaultValueUpdate={() => {}}
                                                height="10rem"
                                                onChange={(e) => {
                                                    if (e.connectorDetails && Array.isArray(e.connectorDetails)) {
                                                        setUpdateDefaultValueEditor(true);
                                                        const tempConnectorDetails = Array(isNaN(e.connectors) ? 0 : e.connectors)
                                                            .fill(null)
                                                            .map((_v, i) => {
                                                                const curVal = _.get(e, ['connectorDetails', i]);
                                                                return curVal
                                                                    ? curVal
                                                                    : {
                                                                          connectorId: 'Enter value here',
                                                                          maxOutputKW: 0,
                                                                          minOutputKW: 0,
                                                                      };
                                                            });
                                                        const tempE = { ...e };
                                                        _.set(tempE, 'connectorDetails', tempConnectorDetails);
                                                        props.setFieldValue('meta', tempE);
                                                        props.setFieldValue('metaErrorKeys', tempE);
                                                    } else {
                                                        props.setFieldValue('metaErrorKeys', e);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {_.get(licenseTypesHashMap, _.get(values, ['licenseId']), '') === 'smartCharging' &&
                                    _.get(values, ['meta', 'sendOCPIChargingProfile'], false) === true ? (
                                        <div>
                                            <div className={classes.FieldControl2}>
                                                <label>Daily Scheduler</label>
                                            </div>
                                            {daysForSchedule.map((day) => (
                                                <div key={day} className={classes.InputContainer}>
                                                    <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                        <label>{day} Plugin time</label>
                                                        <TimePickerWithFormik
                                                            time={values.dailyChargingSchedule[day]?.pluginTime}
                                                            name={`dailyChargingSchedule.${day}.pluginTime`}
                                                            format="HH:mm"
                                                        />
                                                    </div>
                                                    <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                        <label>{day} Plugout time</label>
                                                        <TimePickerWithFormik
                                                            time={values.dailyChargingSchedule[day]?.plugoutTime}
                                                            name={`dailyChargingSchedule.${day}.plugoutTime`}
                                                            format="HH:mm"
                                                        />
                                                    </div>
                                                    <div className={classes.FieldControl2 + ' ' + classes.FieldControlGrow}>
                                                        <label>Total hours</label>
                                                        <p className={instanceClasses.TotalHourField}>
                                                            {findDifferenceInHoursAndMinutesFormatted(
                                                                values.dailyChargingSchedule[day]?.pluginTime,
                                                                values.dailyChargingSchedule[day]?.plugoutTime
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}

                                    <div>
                                        {_.values(errors.metaErrorKeys).map((e) => (
                                            <div>
                                                {Array.isArray(e) ? (
                                                    <>
                                                        {e.map((v) =>
                                                            v === 'Invalid Instance ID' ? (
                                                                <div className="error-msg">{v}</div>
                                                            ) : (
                                                                _.values(v).map((ve) => <div className="error-msg">{ve?.replace('metaErrorKeys', 'meta')}</div>)
                                                            )
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="error-msg">{e?.replace('metaErrorKeys', 'meta')}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div>
                                        {values.market.length ? (
                                            <div className={classes.FieldControl2}>
                                                <label for="approval">Approved Capacity (MW)</label>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                        <div className={instanceClasses.ApprovedCapacityContainer}>
                                            {values.market.map((item) => (
                                                <div className={classes.FieldControl2}>
                                                    <label>{item.value}</label>
                                                    <Input
                                                        name={`approvedCapacity.${item.value}`}
                                                        id={`approvedCapacity.${item.value}`}
                                                        type="number"
                                                        style={{
                                                            marginTop: '0',
                                                            padding: '0.1vw 0 0.1vw 0.5vw',
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        {values.market.length ? (
                                            <div className={classes.FieldControl2}>
                                                <label for="inventoryValue">Inventory Value</label>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                        <div className={instanceClasses.ApprovedCapacityContainer}>
                                            {values.market.map((item) => (
                                                <div className={classes.FieldControl2}>
                                                    <label className={instanceClasses.InventoryLabel}>
                                                        <span>{item.value} </span>
                                                        <span
                                                            onClick={() => {
                                                                setEditInventory((prev) => ({ ...prev, [item.value]: false }));
                                                            }}
                                                            className={instanceClasses.InventoryEditLabel}
                                                        >
                                                            Edit
                                                        </span>
                                                    </label>

                                                    <Input
                                                        name={`inventoryValue.${item.value}`}
                                                        id={`inventoryValue.${item.value}`}
                                                        type="number"
                                                        style={{
                                                            marginTop: '0',
                                                            padding: '0.1vw 0 0.1vw 0.5vw',
                                                        }}
                                                        onBlur={() => {
                                                            const markets = getMarketOptions(customer?.biddingMarkets, values.inventoryValue);
                                                            setMarkets(markets || []);
                                                        }}
                                                        disabled={_.get(editInventory, [item.value])}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* <div className={instanceClasses.ApprovedCapacityContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label>
                                                Inventory Value
                                                <span className="required">*</span>
                                            </label>
                                            <Input
                                                name="inventoryValue"
                                                id="inventoryValue"
                                                type="number"
                                                style={{
                                                    marginTop: '0',
                                                    padding: '0.1vw 0 0.1vw 0.5vw',
                                                }}
                                            />
                                        </div>
                                    </div> */}

                                    <div>
                                        <FieldArray name="webshopUrl">
                                            {({ push, remove, form }) => {
                                                const webshopUrl = form.values.webshopUrl;
                                                return (
                                                    <div>
                                                        <div>
                                                            {webshopUrl.map((e, index) => (
                                                                <div className={classes.FieldControl2}>
                                                                    <label>Webshop URL</label>
                                                                    <Input
                                                                        name={`webshopUrl[${index}]`}
                                                                        id={`webshopUrl[${index}]`}
                                                                        type="text"
                                                                        style={{
                                                                            marginTop: '0',
                                                                            padding: '0.1vw 0 0.1vw 0.5vw',
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className={classes.AddRemoveIconContainer}>
                                                            {webshopUrl.length > 0 && (
                                                                <span onClick={() => remove(webshopUrl.length - 1)}>
                                                                    <UilMinusCircle
                                                                        size={'1.5vw'}
                                                                        style={{
                                                                            color: 'var(--color-primary)',
                                                                            cursor: 'pointer',
                                                                            zIndex: '1',
                                                                        }}
                                                                    />
                                                                </span>
                                                            )}
                                                            <span onClick={() => push('')}>
                                                                <UilPlusCircle
                                                                    size={'1.5vw'}
                                                                    style={{
                                                                        color: 'var(--color-primary)',
                                                                        cursor: 'pointer',
                                                                        zIndex: '1',
                                                                    }}
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        </FieldArray>
                                    </div>
                                    <div className={classes.FieldControl2}>
                                        <label>Jira Link</label>
                                        <Input
                                            name="jiraLink"
                                            id="jiraLink"
                                            type="text"
                                            style={{
                                                marginTop: '0',
                                                padding: '0.1vw 0 0.1vw 0.5vw',
                                            }}
                                        />
                                    </div>
                                    {values.market.some((m) => m.value.includes('aFRR') || m.value.includes('mFRR')) ? (
                                        <div className={classes.FieldControl2}>
                                            <label>
                                                Geo Tag<span className="required">*</span>
                                            </label>
                                            <DropdownComponent
                                                name="geoTag"
                                                id="geoTag"
                                                options={getGeoTagOption(values?.market || [])}
                                                defaultValue={{ value: initialValues?.geoTag }}
                                            />
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                    {values.market.some((m) => m.value.includes('aFRR') || m.value.includes('mFRR')) ? (
                                        <div className={classes.FieldControl2}>
                                            <label>
                                                Fuel Type<span className="required">*</span>
                                            </label>
                                            <DropdownComponent name="fuelType" id="fuelType" options={FUEL_TYPE} defaultValue={{ value: initialValues?.fuelType }} />
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                    <div className={classes.FieldControl2}>
                                        <label for="approval">Address</label>
                                    </div>
                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label>Floor</label>
                                            <Input
                                                name="address.floor"
                                                id="floor"
                                                type="text"
                                                style={{
                                                    marginTop: '0',
                                                    padding: '0.1vw 0 0.1vw 0.5vw',
                                                }}
                                            />
                                        </div>
                                        <div className={classes.FieldControl2}>
                                            <label>Street Name</label>
                                            <Input
                                                name="address.streetName"
                                                id="streetName"
                                                type="text"
                                                style={{
                                                    marginTop: '0',
                                                    padding: '0.1vw 0 0.1vw 0.5vw',
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label>City</label>
                                            <Input
                                                name="address.city"
                                                id="city"
                                                type="text"
                                                style={{
                                                    marginTop: '0',
                                                    padding: '0.1vw 0 0.1vw 0.5vw',
                                                }}
                                            />
                                        </div>
                                        <div className={classes.FieldControl2}>
                                            <label>Pincode</label>
                                            <Input
                                                name="address.pincode"
                                                id="pincode"
                                                type="number"
                                                style={{
                                                    marginTop: '0',
                                                    padding: '0.1vw 0 0.1vw 0.5vw',
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={classes.InputContainer}>
                                        <div className={classes.FieldControl2}>
                                            <label>Country</label>
                                            <DropdownComponent
                                                name="address.country"
                                                id="licenseId"
                                                options={countryOption}
                                                defaultValue={{ value: initialValues?.address?.country }}
                                            />
                                        </div>
                                        <div className={classes.FieldControl2}></div>
                                    </div>

                                    <div className={classes.InputContainer}>
                                        <ToggleButton
                                            values={values}
                                            label={'In market'}
                                            name={`inMarket`}
                                            onChange={(e) => {
                                                props.setFieldValue('inMarket', e.target.checked);
                                                if (e.target.checked) {
                                                    props.setFieldValue('inLedger', true);
                                                }
                                            }}
                                        />
                                        <ToggleButton
                                            values={values}
                                            label={'In Ledger'}
                                            name={`inLedger`}
                                            disabled={values.inMarket}
                                            sliderStyle={values.inMarket ? { background: '#ccc' } : {}}
                                        />
                                        {values.market.some((m) => m.value.includes('FRR')) ? (
                                            <ToggleButton values={values} label={'Resource Schedule'} name={`resourceSchedule`} />
                                        ) : (
                                            ''
                                        )}
                                        <ToggleButton values={values} label={'Energy Market Only'} name={`energyMarketOnly`} />
                                    </div>
                                    <div className={classes.ButtonContainer}>
                                        <div>
                                            <button type="submit" className="btn-primary">
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </ModalComponent>
            <div className={classes.Header}>
                <div>
                    <Typography content="Instances" />
                    <div className={classes.TableCount}>
                        Total Count :
                        <span>
                            <Typography size="14" content={instances?.length || 0} />
                        </span>
                    </div>
                    <div className={classes.FieldControl} style={{ maxWidth: '15vw' }}>
                        <DropDownWithoutFormik name="license" options={licenseOptions} onChange={setSelectedOption} defaultValue={licenseOptions[0]} />
                    </div>
                </div>
                <div className={instanceClasses.SubHeaderRight}>
                    <div className={classes.FieldControl2} style={{ minWidth: '10vw' }}>
                        <DropDownWithoutFormik name="approval" options={approvalOptions} onChange={setSelectedApproval} defaultValue={selectedApproval} />
                    </div>
                    <button
                        type="submit"
                        className="btn-primary"
                        style={{ marginTop: '0' }}
                        onClick={() =>
                            setInstanceFormModal({
                                status: true,
                                data: '',
                            })
                        }
                    >
                        Create Instance
                    </button>
                </div>
            </div>
            <div>
                <Table
                    head={['S.No', 'Name', 'Instance Id', 'Description', 'License Type', 'Market', 'Action']}
                    keys={['s.no', 'name', 'instanceId', 'description', 'licenseType', 'market', 'action']}
                    data={instances}
                />
            </div>
        </div>
    );
}

export default Instances;
